import { Actor, type HttpAgent } from "@icp-sdk/core/agent";
import { Principal } from "@icp-sdk/core/principal";
import type * as OdinSiwbTypes from "@/lib/idl/odin_siwb_idl";
import { type _SERVICE, idlFactory } from "@/lib/idl/odin_siwb_idl";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const clientLog = createLogAdapter("lib/clients/OdinSiwbClient.ts");

/**
 * Odin SIWB canister 客户端，封装所有 SIWB 相关的操作
 */
export class OdinSiwbClient {
	private actor: _SERVICE;
	private canisterId = "bcxqa-kqaaa-aaaak-qotba-cai"; // OdinSiwb canister ID

	constructor(agent: HttpAgent) {
		try {
			// 验证 canister ID 是否有效
			Principal.fromText(this.canisterId);

			this.actor = Actor.createActor<_SERVICE>(idlFactory, {
				agent,
				canisterId: this.canisterId,
			});
		} catch (error) {
			clientLog.error("Failed to initialize OdinSiwbClient:", error);
			throw new Error(`Invalid canister ID: ${this.canisterId}`);
		}
	}

	// ================= Service Methods =================

	/**
	 * 获取指定 Principal 对应的比特币地址
	 * @param principal 主体标识符
	 * @param network 网络类型（可选）
	 * @returns 地址获取响应
	 */
	async get_address(principal: Principal, network?: string): Promise<string> {
		try {
			const response = await this.actor.get_address(
				principal.toUint8Array() as any,
				network || "",
			);

			if ("Ok" in response) {
				return response.Ok;
			} else {
				throw new Error(response.Err);
			}
		} catch (error) {
			clientLog.error("Error getting address:", error);
			throw error;
		}
	}

	/**
	 * 获取调用者的比特币地址
	 * @param network 网络类型（可选）
	 * @returns 地址获取响应
	 */
	async get_caller_address(network?: string): Promise<string> {
		try {
			const response = await this.actor.get_caller_address(
				network ? [network] : [],
			);

			if ("Ok" in response) {
				return response.Ok;
			} else {
				throw new Error(response.Err);
			}
		} catch (error) {
			clientLog.error("Error getting caller address:", error);
			throw error;
		}
	}

	/**
	 * 根据比特币地址获取对应的 Principal
	 * @param address 比特币地址
	 * @returns Principal 获取响应
	 */
	async get_principal(address: string): Promise<any> {
		try {
			if (!address || typeof address !== "string") {
				throw new Error("Invalid address provided");
			}

			clientLog.info(`Calling get_principal for address: ${address}`);
			const response = await this.actor.get_principal(address);
			clientLog.info(`Raw response from get_principal:`, response);

			return response;
		} catch (error) {
			clientLog.error("Error getting principal for address:", address, error);
			throw error;
		}
	}

	/**
	 * 根据比特币地址获取对应的 Principal（已解码为文本格式）
	 * @param address 比特币地址
	 * @returns 解码后的 Principal 字符串，格式如：xkcsw-sd3r4-pbo77-d7vtb-xy2nx-bpm5p-izpqi-3s7xy-2o6pf-hc2dy-dqe
	 */
	async get_principal_decoded(address: string): Promise<string> {
		try {
			if (!address || typeof address !== "string") {
				throw new Error("Invalid address provided");
			}

			clientLog.info(`Fetching PID for address: ${address}`);

			const response = await this.actor.get_principal(address);
			clientLog.info(
				`Full response structure:`,
				JSON.stringify(response, null, 2),
			);

			if ("Ok" in response) {
				const principalBytes = response.Ok;
				clientLog.info(`Principal bytes type:`, typeof principalBytes);
				clientLog.info(`Principal bytes:`, principalBytes);

				// 处理不同的响应格式
				let bytesArray: number[];

				if (Array.isArray(principalBytes)) {
					// 如果已经是数组
					bytesArray = principalBytes;
				} else if (principalBytes instanceof Uint8Array) {
					// 如果是 Uint8Array
					bytesArray = Array.from(principalBytes);
				} else if (
					typeof principalBytes === "object" &&
					principalBytes !== null
				) {
					// 如果是对象，尝试提取数组
						if ("0" in principalBytes) {
							// 可能是类似 {0: 1, 1: 2, 2: 3} 的格式
							const bytesRecord = principalBytes as Record<string, number>;
							const keys = Object.keys(bytesRecord)
								.map(Number)
								.sort((a, b) => a - b);
							bytesArray = keys.map((key) => bytesRecord[String(key)]);
						} else {
							throw new Error(
								`Unexpected principal bytes object format: ${JSON.stringify(principalBytes)}`,
							);
					}
				} else {
					throw new Error(
						`Unexpected principal bytes type: ${typeof principalBytes}, value: ${principalBytes}`,
					);
				}

				clientLog.info(`Processed bytes array:`, bytesArray);

				// 验证字节数组
				if (!Array.isArray(bytesArray) || bytesArray.length === 0) {
					throw new Error(
						`Invalid principal bytes array: ${JSON.stringify(bytesArray)}`,
					);
				}

				// 验证字节值是否有效（0-255）
				const invalidBytes = bytesArray.filter(
					(byte) => typeof byte !== "number" || byte < 0 || byte > 255,
				);
				if (invalidBytes.length > 0) {
					throw new Error(
						`Invalid byte values found: ${JSON.stringify(invalidBytes)}`,
					);
				}

				try {
					const principal = Principal.fromUint8Array(
						new Uint8Array(bytesArray),
					);
					const principalText = principal.toString();

					clientLog.info(
						`Successfully converted address ${address} to PID: ${principalText}`,
					);
					return principalText;
					} catch (principalError) {
						clientLog.error(
							`Failed to create Principal from bytes:`,
							principalError,
						);
						const principalMessage =
							principalError instanceof Error
								? principalError.message
								: String(principalError);
						throw new Error(`Failed to create Principal: ${principalMessage}`);
					}
			} else if ("Err" in response) {
				clientLog.error(
					`API returned error for address ${address}:`,
					response.Err,
				);
				throw new Error(`API Error: ${response.Err}`);
			} else {
				clientLog.error(`Unexpected response format:`, response);
				throw new Error(
					`Unexpected response format: ${JSON.stringify(response)}`,
				);
			}
			} catch (error) {
				clientLog.error(`Error getting principal for address ${address}:`, error);

				// 提供更友好的错误信息
				const errorMessage =
					error instanceof Error ? error.message : String(error);
				if (errorMessage.includes("Invalid principal bytes")) {
					throw new Error(
						`Unable to decode principal for address ${address}. The address may not be registered.`,
					);
				} else if (errorMessage.includes("API Error")) {
					throw new Error(`Service error: ${errorMessage}`);
				} else {
					throw new Error(
						`Failed to get principal for address ${address}: ${errorMessage}`,
					);
				}
			}
	}

	/**
	 * 清理过期的签名数据
	 * @returns void
	 */
	async prune_sigs(): Promise<void> {
		try {
			return await this.actor.prune_sigs();
		} catch (error) {
			clientLog.error("Error pruning signatures:", error);
			throw error;
		}
	}

	/**
	 * 获取 SIWB 委托
	 * @param address 比特币地址
	 * @param sessionKey 会话密钥
	 * @param expiration 过期时间戳
	 * @returns 委托获取响应
	 */
	async siwb_get_delegation(
		address: string,
		sessionKey: OdinSiwbTypes.SessionKey,
		expiration: OdinSiwbTypes.Timestamp,
	): Promise<any> {
		try {
			return await this.actor.siwb_get_delegation(
				address,
				sessionKey,
				expiration,
			);
		} catch (error) {
			clientLog.error("Error getting delegation:", error);
			throw error;
		}
	}

	/**
	 * SIWB 登录
	 * @param signature SIWB 签名
	 * @param address 比特币地址
	 * @param publicKeyHex 公钥十六进制字符串
	 * @param sessionKey 会话密钥
	 * @param signMessageType 签名消息类型
	 * @returns 登录响应
	 */
	async siwb_login(
		signature: OdinSiwbTypes.SiwbSignature,
		address: string,
		publicKeyHex: OdinSiwbTypes.PublickeyHex,
		sessionKey: OdinSiwbTypes.SessionKey,
		signMessageType: OdinSiwbTypes.SignMessageType,
	): Promise<any> {
		try {
			return await this.actor.siwb_login(
				signature,
				address,
				publicKeyHex,
				sessionKey,
				signMessageType,
			);
		} catch (error) {
			clientLog.error("Error during SIWB login:", error);
			throw error;
		}
	}

	/**
	 * 准备 SIWB 登录
	 * @param address 比特币地址
	 * @returns 准备登录响应（包含待签名消息）
	 */
	async siwb_prepare_login(address: string): Promise<any> {
		try {
			return await this.actor.siwb_prepare_login(address);
		} catch (error) {
			clientLog.error("Error preparing SIWB login:", error);
			throw error;
		}
	}

	// ================= 便捷方法 =================

	/**
	 * 完整的 SIWB 登录流程
	 * @param address 比特币地址
	 * @param signFunction 签名函数，接收消息返回签名
	 * @param publicKeyHex 公钥十六进制字符串
	 * @param sessionKey 会话密钥
	 * @param signMessageType 签名消息类型，默认为 ECDSA
	 * @returns 登录详情
	 */
	async performSiwbLogin(
		address: string,
		signFunction: (message: string) => Promise<string>,
		publicKeyHex: OdinSiwbTypes.PublickeyHex,
		sessionKey: OdinSiwbTypes.SessionKey,
		signMessageType: OdinSiwbTypes.SignMessageType = { ECDSA: null },
	): Promise<any> {
		try {
			// 1. 准备登录，获取待签名消息
			const prepareResponse = await this.siwb_prepare_login(address);

			if ("Err" in prepareResponse) {
				throw new Error(`准备登录失败: ${prepareResponse.Err}`);
			}

			const messageToSign = prepareResponse.Ok;

			// 2. 签名消息
			const signature = await signFunction(messageToSign);

			// 3. 执行登录
			const loginResponse = await this.siwb_login(
				signature,
				address,
				publicKeyHex,
				sessionKey,
				signMessageType,
			);

			if ("Err" in loginResponse) {
				throw new Error(`登录失败: ${loginResponse.Err}`);
			}

			return loginResponse.Ok;
		} catch (error) {
			clientLog.error("Error in SIWB login flow:", error);
			throw error;
		}
	}

	/**
	 * 获取会话委托
	 * @param address 比特币地址
	 * @param sessionKey 会话密钥
	 * @param expiration 过期时间戳
	 * @returns 签名委托
	 */
	async getSessionDelegation(
		address: string,
		sessionKey: OdinSiwbTypes.SessionKey,
		expiration: OdinSiwbTypes.Timestamp,
	): Promise<any> {
		try {
			const delegationResponse = await this.siwb_get_delegation(
				address,
				sessionKey,
				expiration,
			);

			if ("Err" in delegationResponse) {
				throw new Error(`获取委托失败: ${delegationResponse.Err}`);
			}

			return delegationResponse.Ok;
		} catch (error) {
			clientLog.error("Error getting session delegation:", error);
			throw error;
		}
	}

	/**
	 * 地址与 Principal 双向转换
	 * @param addressOrPrincipal 地址或 Principal
	 * @returns 转换结果
	 */
	async convertAddressPrincipal(
		addressOrPrincipal: string | Principal,
	): Promise<{
		address?: string;
		principal?: Principal;
		principalText?: string;
	}> {
		try {
			if (typeof addressOrPrincipal === "string") {
				// 地址转 Principal
				const principalResponse = await this.get_principal(addressOrPrincipal);
				if ("Err" in principalResponse) {
					throw new Error(`地址转Principal失败: ${principalResponse.Err}`);
				}

				// 解码 Principal
				const principalBytes = principalResponse.Ok;
				const principal = Principal.fromUint8Array(
					new Uint8Array(principalBytes),
				);

				return {
					principal: principal,
					principalText: principal.toString(),
				};
			} else {
				// Principal 转地址
				const addressResponse = await this.get_address(addressOrPrincipal);
				return { address: addressResponse };
			}
		} catch (error) {
			clientLog.error("Error in address/principal conversion:", error);
			throw error;
		}
	}
}
